import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { type Editor } from '@tiptap/react';
import {
  ChevronDown,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Pilcrow,
  type LucideIcon,
} from 'lucide-react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { setHeadingOnSelection } from '../core';
import { Toolbar } from './toolbar-namespace';

/** Inline heading levels the picker can set — mirrors `INLINE_HEADING_LEVELS`
 * in the extension kit (`# `/H1 is the section delimiter, not a block style). */
const HEADING_LEVELS = [2, 3, 4, 5] as const;
type HeadingLevel = (typeof HEADING_LEVELS)[number];

const HEADING_ICONS: Record<HeadingLevel, LucideIcon> = {
  2: Heading2,
  3: Heading3,
  4: Heading4,
  5: Heading5,
};

/** Radio value for "Text" (paragraph); heading values are the level as a string. */
const PARAGRAPH_VALUE = 'paragraph';

/**
 * Block-style picker for the markdown-native bubble menu — the "turn into"
 * dropdown that lets a paragraph become an H2–H5 heading (and back to text)
 * without typing markdown. A single compact trigger keeps the already-long
 * bubble menu from growing four buttons; the vertical list mirrors the retiring
 * v1 `ContentTypePicker`. The caller gates rendering (hidden whenever the
 * selection touches a section title, list, or code block — see
 * `selectionTouchesProtectedBlock`), so the picker only ever restyles
 * paragraphs and inline headings.
 */
export const HeadingPicker = ({
  editor,
  activeLevel,
}: {
  editor: Editor;
  /** The active inline heading level (2–5), or null when in a paragraph. */
  activeLevel: HeadingLevel | null;
}) => {
  const intl = useIntl();

  const onValueChange = useCallback(
    (value: string) => {
      if (value === PARAGRAPH_VALUE) {
        editor.chain().focus().setParagraph().run();
        return;
      }
      const level = Number(value) as HeadingLevel;
      const headingType = editor.schema.nodes.heading;
      if (!headingType) return;
      // Apply the heading to just the highlighted text: a mid-paragraph
      // selection is split into its own block first, so the surrounding text is
      // untouched (see `setHeadingOnSelection`).
      editor
        .chain()
        .focus()
        .command(({ tr, dispatch }) => {
          if (dispatch) setHeadingOnSelection(tr, headingType, level);
          return true;
        })
        .run();
    },
    [editor]
  );

  const ActiveIcon = activeLevel ? HEADING_ICONS[activeLevel] : Pilcrow;

  return (
    // `modal={false}` is required inside the TipTap bubble menu. A modal
    // DropdownMenu sets `pointer-events: none` on the body and traps focus on
    // open, which blurs the editor — the bubble menu then hides and remounts,
    // detaching the trigger. Radix's popper anchor is left pointing at the
    // now-0×0 detached node, so it drops the menu in the viewport's top-left
    // corner. Non-modal (matching the sibling link `Popover`) keeps the editor
    // focused and the anchor valid.
    <DropdownMenu modal={false}>
      {/* The button is the trigger directly (no wrapping span, no tooltip): the
          dropdown anchors to its real DOM node, and a tooltip on the trigger
          renders as the outer element, which leaves Radix measuring an empty
          rect and floats the menu to the viewport corner. `aria-label` carries
          the accessible name in the tooltip's place. */}
      <DropdownMenuTrigger asChild>
        <Toolbar.Button
          aria-label={intl.formatMessage({
            defaultMessage: 'Text style',
            id: 'hBrEtpUWhZ',
          })}
          iconOnly={false}
          type="button"
          data-testid="text-menu-heading-picker"
        >
          <ActiveIcon size={14} />
          <ChevronDown size={12} />
        </Toolbar.Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup
          value={activeLevel ? String(activeLevel) : PARAGRAPH_VALUE}
          onValueChange={onValueChange}
        >
          <DropdownMenuRadioItem
            value={PARAGRAPH_VALUE}
            className="gap-2"
            data-testid="text-menu-heading-picker-text"
          >
            <Pilcrow className="size-4 shrink-0" />
            {intl.formatMessage({ defaultMessage: 'Text', id: 'aA8bDwsPeJ' })}
          </DropdownMenuRadioItem>
          {HEADING_LEVELS.map((level) => {
            const Icon = HEADING_ICONS[level];
            return (
              <DropdownMenuRadioItem
                key={level}
                value={String(level)}
                className="gap-2"
                data-testid={`text-menu-heading-picker-h${level}`}
              >
                <Icon className="size-4 shrink-0" />
                {intl.formatMessage(
                  { defaultMessage: 'Heading {level}', id: '/seaK6W9q0' },
                  { level }
                )}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
